import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject
} from '@angular/core';
import { forkJoin } from 'rxjs';
import { CompanyVendor, ViveProgress } from 'src/app/classes/models';
import { WidgetPanelCounts } from 'src/app/interfaces/widget-panel-counts';
import { CompanyVendorService } from 'src/app/services/company-vendor.service';
import { UtilityService } from 'src/app/services/utility.service';
import { ViveProgressService } from 'src/app/services/vive-progress.service';

@Component({
  selector: 'app-vendor-suspensions-widget',
  templateUrl: './vendor-suspensions-widget.component.html',
  styleUrls: ['./vendor-suspensions-widget.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class VendorSuspensionsWidgetComponent {
  private _cdr = inject(ChangeDetectorRef);
  vendorSuspensions: WidgetPanelCounts['data'] = new Map([]);

  widgetPanelCounts: WidgetPanelCounts = {
    title: 'Vendor Suspensions',
    hasData: false,
    data: this.vendorSuspensions
  };

  constructor(
    private CompanyVendorService: CompanyVendorService,
    private ViveProgressService: ViveProgressService,
    private UtilityService: UtilityService
  ) {
    const currentDate: Date = new Date();
    const thirtyDaysAgo: Date = new Date(
      new Date().setDate(currentDate.getDate() - 30)
    );

    const getCountOfVendorsSuspendedForNonRenewal: CompanyVendor | {} = {
      viveStatus: 'Suspended',
      vendorPaid: false,
      feeWaived: false,
      literals: {
        $and: ['getCountOfVendorsSuspendedForNonRenewal']
      }
    };

    const getCountOfVendorsSuspendedForNonRenewalLast30Days:
      CompanyVendor | {} = {
      ...getCountOfVendorsSuspendedForNonRenewal,
      statusDate: {
        $gte: thirtyDaysAgo
      }
    };

    const getVendorsSuspendedFromExpiredInsuranceCount = (days?: number) => {
      const query = {
        viveStatus: 'Suspended',
        viveSubStatus: 'Expired Insurance'
      } as CompanyVendor | {};

      if (days) {
        const formattedDays: Date = new Date(
          new Date().setDate(currentDate.getDate() - days)
        );

        Object.assign(query, {
          statusDate: {
            $gte: formattedDays
          }
        });
      }

      return this.UtilityService.stringifyObjectsInQueryObject(query);
    };

    const getMonthlyCountOfSuspendedVendorsWithViveProgressFails = (
      type: ViveProgress['type']
    ) => {
      return this.UtilityService.stringifyObjectsInQueryObject({
        status: 'failed',
        type,
        literals: {
          $and: ['monthlyCountOfSuspendedVendorsWithViveProgressFails']
        }
      } as ViveProgress | {});
    };

    forkJoin([
      this.CompanyVendorService.getCompanyVendorCount(
        this.UtilityService.stringifyObjectsInQueryObject(
          getCountOfVendorsSuspendedForNonRenewal
        )
      ),
      this.CompanyVendorService.getCompanyVendorCount(
        this.UtilityService.stringifyObjectsInQueryObject(
          getCountOfVendorsSuspendedForNonRenewalLast30Days
        )
      ),
      this.ViveProgressService.getViveProgressCount(
        getMonthlyCountOfSuspendedVendorsWithViveProgressFails('Vendor Info')
      ),
      this.ViveProgressService.getViveProgressCount(
        getMonthlyCountOfSuspendedVendorsWithViveProgressFails('Owner Info')
      ),
      this.ViveProgressService.getViveProgressCount(
        getMonthlyCountOfSuspendedVendorsWithViveProgressFails(
          'Trades & Licenses'
        )
      ),
      this.ViveProgressService.getViveProgressCount(
        getMonthlyCountOfSuspendedVendorsWithViveProgressFails('Insurance')
      ),
      this.CompanyVendorService.getCompanyVendorCount(
        getVendorsSuspendedFromExpiredInsuranceCount()
      ),
      this.CompanyVendorService.getCompanyVendorCount(
        getVendorsSuspendedFromExpiredInsuranceCount(30)
      )
    ]).subscribe({
      next: ([
        countOfVendorsSuspendedForNonRenewal,
        countOfVendorsSuspendedForNonRenewalLast30Days,
        monthlyCountOfSuspendedVendorsWithViveProgressFailsBusinessInfo,
        monthlyCountOfSuspendedVendorsWithViveProgressFailsOwner,
        monthlyCountOfSuspendedVendorsWithViveProgressFailsTrade,
        monthlyCountOfSuspendedVendorsWithViveProgressFailsInsurance,
        vendorsSuspendedFromExpiredInsuranceCount,
        vendorsSuspendedFromExpiredInsuranceCountLast30Days
      ]) => {
        this.vendorSuspensions.set('Suspended From Non Renewal', {
          count: Number(countOfVendorsSuspendedForNonRenewal),
          query: getCountOfVendorsSuspendedForNonRenewal
        });
        this.vendorSuspensions.set(
          'Suspended From Non Renewal - Last 30 Days',
          {
            count: Number(countOfVendorsSuspendedForNonRenewalLast30Days),
            query: getCountOfVendorsSuspendedForNonRenewalLast30Days
          }
        );
        this.vendorSuspensions.set(
          'Suspended Last 30 Days with Business Fails',
          {
            count: Number(
              monthlyCountOfSuspendedVendorsWithViveProgressFailsBusinessInfo
            ),
            query:
              getMonthlyCountOfSuspendedVendorsWithViveProgressFails(
                'Vendor Info'
              )
          }
        );
        this.vendorSuspensions.set('Suspended Last 30 Days with Owner Fails', {
          count: Number(
            monthlyCountOfSuspendedVendorsWithViveProgressFailsOwner
          ),
          query:
            getMonthlyCountOfSuspendedVendorsWithViveProgressFails('Owner Info')
        });
        this.vendorSuspensions.set('Suspended Last 30 Days with Trade Fails', {
          count: Number(
            monthlyCountOfSuspendedVendorsWithViveProgressFailsTrade
          ),
          query:
            getMonthlyCountOfSuspendedVendorsWithViveProgressFails(
              'Trades & Licenses'
            )
        });
        this.vendorSuspensions.set(
          'Suspended Last 30 Days with Insurance Fails',
          {
            count: Number(
              monthlyCountOfSuspendedVendorsWithViveProgressFailsInsurance
            ),
            query:
              getMonthlyCountOfSuspendedVendorsWithViveProgressFails(
                'Insurance'
              )
          }
        );
        (this.vendorSuspensions.set('Suspended From Expired Insurance', {
          count: Number(vendorsSuspendedFromExpiredInsuranceCount),
          query: getVendorsSuspendedFromExpiredInsuranceCount()
        }),
          this.vendorSuspensions.set(
            'Suspended From Expired Insurance Last 30 Days',
            {
              count: Number(
                vendorsSuspendedFromExpiredInsuranceCountLast30Days
              ),
              query: getVendorsSuspendedFromExpiredInsuranceCount(30)
            }
          ));
        this._cdr.markForCheck();
      }
    });
  }
}
